import { Injectable, inject } from '@angular/core';
import { CanActivate, Router, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { Observable } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { RoleService } from '../services/role.service';
import { LoggerService } from '../services/logger.service';
import { UserRole } from '../shared/types/role.interface';

/**
 * Route guard that enforces access control based on authentication and user role, redirecting unauthorized users to login or their role-appropriate dashboard.
 * @component
 * @selector app-role-guard
 * @example
 *   // Usage in route configuration
 *   { path: 'admin', canActivate: [RoleGuard] }
 * @input none - This guard does not expose Angular @Input properties; it operates using injected services (AuthService, RoleService, Router, LoggerService).
 * @output none - This guard does not emit Angular @Output events; it performs navigation side-effects via Router.
 *
 * Purpose:
 *   - Ensures that only authenticated users with the appropriate role can activate routes.
 *   - Prevents SUPER_ADMIN users from accessing non-admin routes and prevents non-super-admins from accessing /admin routes.
 *   - Redirects unauthenticated users to the login page with a returnUrl query parameter.
 *
 * Injected services:
 *   - AuthService: used to retrieve current user and token information.
 *   - RoleService: used to determine the current user role, default route for a role, and whether a role has permission for a route.
 *   - Router: used to perform redirects when access is denied.
 *   - LoggerService: used to log info, warning, and debug messages for auditing and troubleshooting.
 *
 * Key methods:
 *   - canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> | Promise<boolean> | boolean
 *       - Main entry point invoked by Angular routing to decide if a route may be activated.
 *       - Checks for a valid user token; if missing, logs and redirects to login.
 *       - Resolves the current user role and denies access if no role is found.
 *       - Normalizes the requested URL (strips query params and fragments) for permission checks.
 *       - Enforces role boundaries:
 *           - Redirects SUPER_ADMIN away from non-admin routes to /admin/dashboard.
 *           - Redirects non-super-admins away from /admin routes to their role default route.
 *       - Uses RoleService.hasRoutePermission to decide final access; if denied, redirects to role default route.
 *       - Logs access decisions at appropriate severity levels (info, warn, debug).
 *       - Returns true if access is granted, otherwise returns false after performing navigation side-effects.
 *
 *   - redirectToLogin(returnUrl: string): void
 *       - Helper that logs the redirect and navigates to /login with a returnUrl query parameter so the user can be returned after authentication.
 *
 * Important lifecycle behavior:
 *   - This class is providedIn: 'root' and is instantiated/injected by Angular as a singleton guard.
 *   - It has no Angular component lifecycle hooks; its behavior is triggered by the router when evaluating route activation.
 */
@Injectable({
  providedIn: 'root',
})
export class RoleGuard implements CanActivate {
  private authService = inject(AuthService);
  private roleService = inject(RoleService);
  private router = inject(Router);
  private logger = inject(LoggerService);

  /**
   * Guard that determines whether navigation to a route is allowed based on user authentication and role.
   * @example
   * anActivate(routeSnapshot, routerStateSnapshot)
   * true
   * @param {{ActivatedRouteSnapshot}} {{route}} - Snapshot of the route to be activated containing route params and data.
   * @param {{RouterStateSnapshot}} {{state}} - Router state snapshot containing the requested URL and navigation info.
   * @returns {{Observable<boolean> | Promise<boolean> | boolean}} Boolean or async boolean indicating whether access is permitted.
   **/
  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> | Promise<boolean> | boolean {
    // Check if user data exists in local storage
    const userData = this.authService.user();

    if (!userData || !userData.token) {
      this.logger.info('Access denied - No valid user token', { route: state.url }, 'RoleGuard');
      this.redirectToLogin(state.url);
      return false;
    }

    // Get user role from the role service
    const userRole = this.roleService.getCurrentUserRole();

    if (!userRole) {
      this.logger.warn('Access denied - No user role found', { route: state.url }, 'RoleGuard');
      this.redirectToLogin(state.url);
      return false;
    }

    const [basePath] = state.url.split('?');
    const routePath = basePath.split('#')[0];

    // Prevent super admin from accessing regular routes
    if (userRole === UserRole.SUPER_ADMIN && !routePath.startsWith('/admin')) {
      this.logger.warn(
        'Access denied - Super admin cannot access regular routes',
        {
          originalUrl: state.url,
          routePath: routePath,
          userRole,
        },
        'RoleGuard'
      );
      this.router.navigate(['/admin/dashboard']);
      return false;
    }

    if (userRole !== UserRole.SUPER_ADMIN && routePath.startsWith('/admin')) {
      this.logger.warn(
        'Access denied - Regular users cannot access admin routes',
        {
          originalUrl: state.url,
          routePath: routePath,
          userRole,
        },
        'RoleGuard'
      );
      const defaultRoute = this.roleService.getDefaultRoute(userRole);
      this.router.navigate([defaultRoute]);
      return false;
    }

    // Check if user has permission to access this route
    const hasPermission = this.roleService.hasRoutePermission(routePath, userRole);

    if (!hasPermission) {
      this.logger.warn(
        'Access denied - Insufficient permissions',
        {
          originalUrl: state.url,
          routePath: routePath,
          userRole,
        },
        'RoleGuard'
      );

      // Redirect to appropriate dashboard based on role
      const defaultRoute = this.roleService.getDefaultRoute(userRole);
      this.router.navigate([defaultRoute]);
      return false;
    }

    this.logger.debug(
      'Access granted - User has permission',
      {
        originalUrl: state.url,
        routePath: routePath,
        userRole,
      },
      'RoleGuard'
    );

    return true;
  }

  private redirectToLogin(returnUrl: string): void {
    this.logger.info('Redirecting to login', { returnUrl }, 'RoleGuard');
    this.router.navigate(['/login'], {
      queryParams: { returnUrl },
    });
  }
}
